import { createSlice, PayloadAction, Reducer } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storageLocal from "redux-persist/lib/storage";
import storageSession from "redux-persist/lib/storage/session";
import { resetUserState, setUser, User } from "./userSlice";

// The single model for saved settings: anything we remember on a user's behalf -
// page filters, sorting, paging, a chosen tab, a dismissed notice, a preference.
//
// One rule is baked in for every setting, whatever its lifetime:
//
//   Settings belong to a user id, not to the browser.
//
// An admin who impersonates another user must not carry their own values into
// the impersonated session, because the impersonated user often cannot see the
// district/school/course those values name, which breaks the pages that read
// them. So each identity's values are kept apart: the admin's are set aside and
// handed back when the impersonation ends, and the impersonated user's are
// dropped, since they were never really theirs.
//
// What differs between settings is how long they live, which is the `storage`
// option:
//
//   "session"  sessionStorage. Survives navigation and refresh, dies with the
//              tab, and is cleared on sign-out. For transient page state - the
//              filters, sorts and paging that only make sense within a visit.
//
//   "local"    localStorage. Survives sign-out and closing the browser, and is
//              waiting for the user next time they sign in. For settings the
//              user would expect to stick - a dismissed notice, a preference.
//
//   "memory"   Neither. Lives only as long as the store, and is cleared on
//              sign-out.
//
// To add settings, call createUserSettingsSlice and register the returned
// reducer in store.ts. Nothing else is needed; ownership, persistence and
// clearing all follow from the options.
//
// Deliberately NOT modelled here:
// - accessibility (font size, contrast, overlay, line reader): persistent, but
//   its store of record is the server - it round-trips through GetSelf and the
//   axString, so it already follows identity on its own and is shared with the
//   MVC site. Moving it into browser storage would fork that.
// - contrast: derived from CSS variables at runtime, never stored.
// - "prefersSpa" / "isLegacyLoggedIn": tab and handoff flags rather than user
//   settings. isLegacyLoggedIn is read before any identity is known, so per-user
//   keying would break it outright.
// - dismissed broadcast alerts: genuinely a persistent per-user setting, and the
//   natural first candidate to move here ("local" storage). What stops it today
//   is that its keys are shared with the MVC app (see
//   BroadcastAlerts.getStorageKey), so moving it alone would break parity.

export interface UserSettings<V> {
	/** The user `values` belongs to; null before anyone has signed in. */
	ownerUserId: number | null;
	/**
	 * Was the owner an impersonated identity? Recorded when they took over, so
	 * that when they leave we know their values are not worth keeping. Read from
	 * the setUser payload rather than from the user slice, which keeps the
	 * handover a plain synchronous reducer instead of cross-slice middleware -
	 * and so avoids racing the resets that Logout dispatches straight after.
	 */
	ownerIsImpersonated: boolean;
	/** The current identity's values. This is what pages read and write. */
	values: V;
	/** Values belonging to other identities, by user id. */
	stash: Record<number, V>;
}

// Declares which settings depend on which. When a setting changes, whatever
// depends on it is reset to its initial value, transitively: given
// { districtFilter: ["schoolFilter"], schoolFilter: ["teacherFilter"] }, changing
// the district clears the school and, through it, the teacher.
//
// A value the same update set explicitly is left alone, so one update that moves
// both the district and the school does not immediately blank the school.
export type SettingDependents<V> = Partial<Record<keyof V, (keyof V)[]>>;

export interface UserSettingsOptions<V> {
	/** Slice name, and the basis of the storage key when persisted. */
	name: string;
	initialValues: V;
	/** How long these settings live. See the note above. Defaults to "session". */
	storage?: "session" | "local" | "memory";
	dependents?: SettingDependents<V>;
}

function initialSettings<V>(initialValues: V): UserSettings<V> {
	return {
		ownerUserId: null,
		ownerIsImpersonated: false,
		values: { ...initialValues },
		stash: {},
	};
}

// Reset everything that (transitively) depends on a setting the update changed.
function applyDependents<V extends object>(
	next: V,
	previous: V,
	patch: Partial<V>,
	dependents: SettingDependents<V>,
	initialValues: V
): V {
	const result = { ...next };
	const queue = (Object.keys(result) as (keyof V)[]).filter((k) => result[k] !== previous[k]);
	const visited = new Set<keyof V>();

	while (queue.length) {
		const changed = queue.shift()!;
		if (visited.has(changed)) continue;
		visited.add(changed);

		for (const dependent of dependents[changed] ?? []) {
			// The caller set this one deliberately in the same update, so it is not
			// stale - and clearing it would throw away what they just asked for.
			if (dependent in patch) continue;
			if (result[dependent] === initialValues[dependent]) continue;
			result[dependent] = initialValues[dependent];
			queue.push(dependent);
		}
	}

	return result;
}

export function createUserSettingsSlice<V extends object>(options: UserSettingsOptions<V>) {
	const { name, initialValues, storage = "session", dependents } = options;

	const slice = createSlice({
		name,
		initialState: initialSettings(initialValues),
		reducers: {
			// The one way to write settings. Takes a partial update, so a page can
			// change one value or several at once without a setter per field.
			set: (state, action: PayloadAction<Partial<V>>) => {
				const previous = state.values as V;
				let next = { ...previous, ...action.payload };
				if (dependents) {
					next = applyDependents(
						next,
						previous,
						action.payload,
						dependents,
						initialValues
					);
				}
				state.values = next as typeof state.values;
			},
			// Clears the current identity's values. Values held for other
			// identities belong to them and are left alone.
			reset: (state) => {
				state.values = { ...initialValues } as typeof state.values;
			},
		},
		extraReducers: (builder) => {
			// Someone else signed in: hand the settings over to them.
			builder.addCase(setUser, (state, action) => {
				const user: Pick<User, "userId" | "isImpersonating"> = action.payload;
				if (state.ownerUserId === user.userId) return;

				// The outgoing values are kept only if they belong to a real
				// identity. An impersonated user's settings die with the
				// impersonation - the admin behind them gets their own back below.
				if (state.ownerUserId !== null && !state.ownerIsImpersonated) {
					state.stash[state.ownerUserId] = state.values;
				}

				// Popped rather than copied, so a restored set never lingers as a
				// stale duplicate that a later handover could resurrect.
				let restored: typeof state.values | undefined;
				if (user.userId !== null) {
					restored = state.stash[user.userId];
					delete state.stash[user.userId];
				}

				state.ownerUserId = user.userId;
				state.ownerIsImpersonated = !!user.isImpersonating;
				state.values = restored ?? ({ ...initialValues } as typeof state.values);
			});

			// Sign-out, or a 401. Settings that were only meant to last the visit go
			// away entirely, stash included, so the next person to sign in on this
			// tab starts clean. Settings stored locally are meant to outlive the
			// session and are left exactly where they are - the handover above is
			// what keeps them apart per user.
			if (storage !== "local") {
				builder.addCase(resetUserState, () => initialSettings(initialValues));
			}
		},
	});

	const sliceReducer = slice.reducer as Reducer<UserSettings<V>>;

	// The key is versioned so a tab still holding an older shape starts clean
	// rather than rehydrating keys nothing reads. Bump this whenever the stored
	// shape changes: a stale entry does not error, it merges, leaving the slice
	// half-populated from a layout nothing reads any more.
	const persistKey = `${name}.v3`;

	const reducer: Reducer<UserSettings<V>> =
		storage === "memory"
			? sliceReducer
			: (persistReducer(
					{
						key: persistKey,
						storage: storage === "local" ? storageLocal : storageSession,
					},
					sliceReducer
			  ) as unknown as Reducer<UserSettings<V>>);

	return {
		/** Register this in store.ts. Already wrapped for persistence if asked for. */
		reducer,
		/** Apply a partial update to the current identity's values. */
		set: slice.actions.set,
		/** Clear the current identity's values back to their initial state. */
		reset: slice.actions.reset,
		/** Read the current identity's values out of this slice's state. */
		selectValues: (state: UserSettings<V>): V => state.values,
	};
}
